import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { CLASSE_COR, classificar, dayBox, GRAVIDADE_COR, GRAVIDADE_LABEL, initials, NOTA_EXPLICACAO, stripe, tintFor, visitBits, fmtTime, fmtDayMon } from '../lib/format';
import type { Gravidade, PhotoItem, VisitSummary } from '../lib/types';

export function Button({ variant, size, block, loading, children, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'dark' | 'accent' | 'danger' | 'danger-solid'; size?: 'lg' | 'sm' | 'xs'; block?: boolean; loading?: boolean;
}) {
  const v = variant === 'danger-solid' ? 'danger solid' : variant ?? '';
  return (
    <button className={`btn ${v} ${size ?? ''} ${block ? 'block' : ''} ${className}`} disabled={loading || rest.disabled} {...rest}>
      {loading && <span className="spinner" aria-hidden />}
      {children}
    </button>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field" style={{ cursor: 'default' }}>
      <span className="label">{label}</span>
      {children}
      {hint && <span className="small" style={{ lineHeight: 1.4 }}>{hint}</span>}
    </label>
  );
}

export function Avatar({ name, size = 32, round = true, color, seed }: { name: string; size?: number; round?: boolean; color?: string; seed?: string }) {
  return (
    <div
      className={`avatar ${round ? 'round' : ''}`}
      style={{ width: size, height: size, borderRadius: round ? '50%' : Math.round(size * 0.28), background: color ?? tintFor(seed ?? name), fontSize: Math.max(10, Math.round(size * 0.34)) }}
      aria-hidden
    >
      {initials(name)}
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" style={{ color: 'var(--accent)' }} aria-label="Carregando" />;
}

export function Loading({ rows = 3, height = 64 }: { rows?: number; height?: number }) {
  return (
    <div className="stack gap-8" aria-busy>
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton" style={{ height }} />)}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="empty">
      <div style={{ marginBottom: onRetry ? 10 : 0 }}>{message}</div>
      {onRetry && <Button size="xs" onClick={onRetry}>Tentar de novo</Button>}
    </div>
  );
}

export function Stat({ value, label, color, small }: { value: ReactNode; label: string; color?: string; small?: boolean }) {
  return (
    <div className={`stat ${small ? 'sm' : ''}`}>
      <div className="n" style={{ color }}>{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, fill, label }: { value: T; options: Array<[T, string]>; onChange: (v: T) => void; fill?: boolean; label?: string }) {
  return (
    <div className={`seg ${fill ? 'fill' : ''}`} role="tablist" aria-label={label}>
      {options.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={value === k} className={value === k ? 'on' : ''} onClick={() => onChange(k)}>{l}</button>
      ))}
    </div>
  );
}

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={`switch ${on ? 'on' : ''}`} onClick={() => onChange(!on)} />;
}

/** Nota 0–100 com a cor da classificação (RDC 275). */
export function NotaBadge({ nota, size = 'sm', showClasse, label = 'Nota média', extra }: {
  nota: number | null | undefined; size?: 'sm' | 'lg'; showClasse?: boolean; label?: string; extra?: ReactNode;
}) {
  if (nota === null || nota === undefined) return null;
  const c = classificar(nota);
  const cor = CLASSE_COR[c];
  if (size === 'lg') {
    return (
      <div className="none" style={{ minWidth: 118, padding: '10px 12px', borderRadius: 12, background: cor.bg, color: cor.fg, textAlign: 'center' }} title={NOTA_EXPLICACAO}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', opacity: 0.85 }}>{label}</div>
        <div className="mono" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.1 }}>{nota}<span style={{ fontSize: 12, opacity: 0.7 }}>/100</span></div>
        <div style={{ fontSize: 12, fontWeight: 600 }}>{c}</div>
        {extra && <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>{extra}</div>}
      </div>
    );
  }
  return (
    <span className="pill" style={{ background: cor.bg, color: cor.fg, fontWeight: 500 }} title={`${c} · ${NOTA_EXPLICACAO}`}>
      {nota}{showClasse ? ` · ${c}` : ''}
    </span>
  );
}

export function GravPill({ g }: { g: Gravidade | undefined }) {
  if (!g) return null;
  const cor = GRAVIDADE_COR[g];
  return <span className="pill upper" style={{ background: cor.bg, color: cor.fg }}>{GRAVIDADE_LABEL[g]}</span>;
}

/** Escolha de gravidade (nova não conformidade). */
export function GravidadePicker({ value, onChange }: { value: Gravidade; onChange: (g: Gravidade) => void }) {
  return (
    <div className="row wrap gap-6" role="radiogroup" aria-label="Gravidade">
      {(['leve', 'moderada', 'grave'] as const).map((g) => (
        <button
          key={g} type="button" role="radio" aria-checked={value === g}
          className="chip toggle"
          style={value === g ? { background: GRAVIDADE_COR[g].bg, color: GRAVIDADE_COR[g].fg, borderColor: GRAVIDADE_COR[g].fg } : undefined}
          onClick={() => onChange(g)}
        >
          {GRAVIDADE_LABEL[g]}
        </button>
      ))}
    </div>
  );
}

/** Linha de visita (Início, Relatórios, Restaurante, Portal). */
export function VisitRow({ v, to, title, showRest = true, cta }: { v: VisitSummary; to: string; title?: string; showRest?: boolean; cta?: string }) {
  const at = v.finishedAt ?? v.startedAt;
  const { day, mon } = dayBox(at);
  return (
    <Link to={to} className="item">
      <div className="date"><div className="d">{day}</div><div className="m">{mon}</div></div>
      <div className="body">
        <div className="t wrapline">{title ?? (showRest ? v.restaurantNome : `Visita nº ${v.numero}${v.authorNome ? ` · ${v.authorNome}` : ''}`)}</div>
        <div className="s wrapline">{v.authorNome && showRest ? `${v.authorNome} · ` : ''}{visitBits(v)}</div>
      </div>
      <div className="none stack gap-4" style={{ alignItems: 'flex-end' }}>
        <NotaBadge nota={v.nota} />
        {cta ? (
          <div style={{ fontSize: 12.5, color: 'var(--accent)', fontWeight: 500 }}>{cta}</div>
        ) : (
          <div className="row gap-4">
            {v.novas > 0 && <span className="pill new">+{v.novas}</span>}
            {v.resolvidas > 0 && <span className="pill res">✓{v.resolvidas}</span>}
          </div>
        )}
      </div>
    </Link>
  );
}

export function PhotoTile({ p, onOpen, stamp }: { p: PhotoItem; onOpen: () => void; stamp?: string }) {
  return (
    <button type="button" className="ph" onClick={onOpen} aria-label={`Abrir foto: ${p.legenda ?? p.setor}`}>
      {/* <img loading="lazy">: só baixa quando a foto aparece na tela (galerias grandes não travam). */}
      <div className="img" style={{ background: stripe(p.id) }}>
        <img src={p.thumbUrl} alt="" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
        <div className="chip-dark">{p.setor}</div>
        {p.isNc && <div className="chip-nc">NC</div>}
        {p.tag && !p.isNc && <div className="chip-tag">{p.tag}</div>}
      </div>
      <div className="cap">
        <div className="t clamp2">{p.legenda || 'Sem legenda'}</div>
        <div className="s">{stamp ?? `${p.visitDate ? fmtDayMon(p.visitDate) + ' · ' : ''}${fmtTime(p.capturedAt)} · ${p.authorNome.split(' ')[0]}`}</div>
      </div>
    </button>
  );
}

// ───────── Sheet: bottom sheet no celular, modal centralizado no desktop ─────────
export function Sheet({ open, onClose, title, subtitle, children, wide, footer }: {
  open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode; children: ReactNode; wide?: boolean; footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    window.addEventListener('keydown', onKey);
    // Foco automático só no desktop: no celular abriria o teclado por cima do sheet.
    const t = window.matchMedia('(min-width: 760px)').matches
      ? setTimeout(() => ref.current?.querySelector<HTMLElement>('input:not([readonly]),select,textarea')?.focus({ preventScroll: true }), 50)
      : undefined;
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`sheet ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <div className="sheet-grip" />
        <div className="sheet-head">
          <div className="flex1">
            <div className="ttl" style={{ flex: 'none' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2, lineHeight: 1.4 }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Fechar">×</button>
        </div>
        {children}
        {footer}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmação simples com o mesmo visual de sheet. */
export function useConfirm() {
  const [state, setState] = useState<null | { title: string; message: ReactNode; cta: string; danger?: boolean; resolve: (v: boolean) => void }>(null);
  const ask = (o: { title: string; message: ReactNode; cta: string; danger?: boolean }) => new Promise<boolean>((resolve) => setState({ ...o, resolve }));
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  const node = (
    <Sheet open={!!state} onClose={() => close(false)} title={state?.title ?? ''}>
      <div className="sub" style={{ marginBottom: 18 }}>{state?.message}</div>
      <div className="row gap-8">
        <Button className="flex1" onClick={() => close(false)}>Voltar</Button>
        <Button className="flex1" variant={state?.danger ? 'danger-solid' : 'dark'} onClick={() => close(true)}>{state?.cta}</Button>
      </div>
    </Sheet>
  );
  return { ask, node };
}

export function EmptyLine({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
